import { makeAutoObservable, runInAction, reaction } from 'mobx';
import { getOnePieceServerApi } from 'gql-ops';
import {
    LuckyDrawUserStats,
    LuckyDrawReward,
    LuckyDrawRoundStats,
    LuckyDrawUserHistoryRecord,
} from 'gql-ops/graph/__generated__/request';
import user from './user';
import { authStore } from './authorizationStore';

export class LuckyDrawStore {
    todayStats: LuckyDrawUserStats | null = null;

    histories: LuckyDrawUserHistoryRecord[] = [];

    historiesTotalCount: number = 0;

    rewards: LuckyDrawReward[] = [];

    currentRoundStats: LuckyDrawRoundStats | null = null;
    loading: {
        todayStats: boolean;
        histories: boolean;
        rewards: boolean;
        currentRoundStats: boolean;
    } = {
        todayStats: false,
        histories: false,
        rewards: false,
        currentRoundStats: false,
    };
    error: {
        todayStats: string | null;
        histories: string | null;
        rewards: string | null;
        currentRoundStats: string | null;
    } = {
        todayStats: null,
        histories: null,
        rewards: null,
        currentRoundStats: null,
    };

    constructor() {
        makeAutoObservable(this);

        reaction(
            () => user.address,
            async (address, previousAddress) => {
                // Clear token when address changes
                if (previousAddress && address && previousAddress.toLowerCase() !== address.toLowerCase()) {
                    authStore.clearToken();
                }

                if (address) {
                    // Wait a bit for token to be ready if needed, then fetch data
                    // Use auth=true to ensure token is generated before API calls
                    this.refreshAllData(address, { page: 1, limit: 10 });
                } else {
                    this.reset();
                }
            },
            { fireImmediately: true },
        );

        this.fetchRewards();
    }

    async fetchRewards() {
        runInAction(() => {
            this.loading.rewards = true;
            this.error.rewards = null;
        });

        try {
            const res = await getOnePieceServerApi().luckyDrawRewards();

            runInAction(() => {
                this.rewards = res.luckyDrawRewards || [];
                this.loading.rewards = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error.rewards = error instanceof Error ? error.message : 'Failed to fetch rewards';
                this.loading.rewards = false;
            });
        }
    }

    async fetchCurrentRoundStats() {
        runInAction(() => {
            this.loading.currentRoundStats = true;
            this.error.currentRoundStats = null;
        });

        try {
            const res = await getOnePieceServerApi().luckyDrawCurrentRoundStats();

            runInAction(() => {
                this.currentRoundStats = res.luckyDrawCurrentRoundStats || null;
                this.loading.currentRoundStats = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error.currentRoundStats =
                    error instanceof Error ? error.message : 'Failed to fetch current round stats';
                this.loading.currentRoundStats = false;
            });
        }
    }

    async fetchTodayStats(address: string) {
        if (!address) return;

        runInAction(() => {
            this.loading.todayStats = true;
            this.error.todayStats = null;
        });

        try {
            // Use auth=true to ensure token is generated/valid before API call
            // This prevents "Invalid authorization token" errors when address changes
            const res = await getOnePieceServerApi(true).LuckyDrawUserStats({
                address,
            });

            runInAction(() => {
                this.todayStats = res.luckyDrawUserStats || null;
                this.loading.todayStats = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error.todayStats = error instanceof Error ? error.message : 'Failed to fetch today stats';
                this.loading.todayStats = false;
            });
        }
    }

    async fetchHistories(address: string, pagination: { page: number; limit: number }) {
        if (!address) return;

        runInAction(() => {
            this.loading.histories = true;
            this.error.histories = null;
        });

        try {
            // Use auth=true to ensure token is generated/valid before API call
            // This prevents "Invalid authorization token" errors when address changes
            const res = await getOnePieceServerApi(true).luckyDrawUserHistories({
                address,
                pagination,
            });

            runInAction(() => {
                const items = res.luckyDrawUserHistories?.items || [];
                const totalCount = res.luckyDrawUserHistories?.pagination?.total || 0;
                this.histories = items;
                this.historiesTotalCount = totalCount;
                this.loading.histories = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error.histories = error instanceof Error ? error.message : 'Failed to fetch histories';
                this.loading.histories = false;
            });
        }
    }

    getRewardByIndex(index: number): LuckyDrawReward | null {
        return this.rewards.find((reward) => Number(reward.index) === Number(index)) || null;
    }

    getRewardByName(name: string): LuckyDrawReward | null {
        return this.rewards.find((reward) => reward.name.toLowerCase().includes(name.toLowerCase())) || null;
    }

    async refreshAllData(address: string, pagination?: { page: number; limit: number }) {
        if (!address) return;

        const promises = [this.fetchTodayStats(address), this.fetchCurrentRoundStats()];

        if (pagination) {
            promises.push(this.fetchHistories(address, pagination));
        }

        await Promise.all(promises);
    }

    reset() {
        runInAction(() => {
            this.todayStats = null;
            this.histories = [];
            this.historiesTotalCount = 0;
            this.currentRoundStats = null;
            this.loading = {
                todayStats: false,
                histories: false,
                rewards: false,
                currentRoundStats: false,
            };
            this.error = {
                todayStats: null,
                histories: null,
                rewards: null,
                currentRoundStats: null,
            };
        });
    }
}
const luckyDrawStore = new LuckyDrawStore();
export default luckyDrawStore;
